'use client'

import React, { FC, useMemo } from 'react'
import Link from 'next/link'
import {
	ArrowUpRight,
	ExternalLink,
	GitBranch,
	Hash,
	Images,
	Layers3,
	ShieldCheck,
	SquareArrowOutUpRight
} from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ORD_ROOT_URL } from '@/utils/constants'
import type { CollectionObjectType } from '@/utils/collection-object'

const integerNumber = new Intl.NumberFormat('en-US', {
	maximumFractionDigits: 0
})

const truncateId = (id?: string | null, head = 8, tail = 6) => {
	if (!id) return ''
	if (id.length <= head + tail + 3) return id
	return `${id.slice(0, head)}...${id.slice(-tail)}`
}

const firstNumber = (...values: any[]) => {
	for (const value of values) {
		const parsed = Number(value)
		if (Number.isFinite(parsed) && parsed >= 0) return parsed
	}

	return undefined
}

const formatInteger = (value?: number) => {
	if (typeof value !== 'number' || !Number.isFinite(value)) return '-'
	return integerNumber.format(value)
}

const normalizeParentIds = (inscription: any) => {
	const candidates = [
		inscription?.parents,
		inscription?.parent_ids,
		inscription?.parent_inscription_ids,
		inscription?.properties?.parents,
		inscription?.properties?.parent_ids
	]
	const ids = candidates.flatMap((value) => {
		if (!Array.isArray(value)) return []
		return value.flatMap((entry) => {
			if (typeof entry === 'string') return [entry]
			if (typeof entry?.id === 'string') return [entry.id]
			if (typeof entry?.inscription_id === 'string') return [entry.inscription_id]
			return []
		})
	})

	if (typeof inscription?.parent === 'string') ids.push(inscription.parent)
	if (typeof inscription?.parent_inscription_id === 'string') ids.push(inscription.parent_inscription_id)

	return Array.from(new Set(ids.filter(Boolean)))
}

const DetailRow: FC<{
	label: string
	children: React.ReactNode
}> = ({ label, children }) => (
	<div className="grid grid-cols-[92px_minmax(0,1fr)] gap-3 border-t border-border py-2.5 first:border-t-0">
		<div className="text-xs font-medium text-muted-foreground">{label}</div>
		<div className="min-w-0 text-xs font-medium text-foreground">{children}</div>
	</div>
)

const HeaderButton: FC<{
	sourceIcon: React.ReactNode
}> = ({ sourceIcon }) => (
	<TooltipProvider delayDuration={150}>
		<Tooltip>
			<TooltipTrigger asChild>
				<PopoverTrigger asChild>
					<button
						type="button"
						className="inline-flex h-6 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-muted px-2.5 text-[11px] font-medium text-muted-foreground transition-colors duration-200 hover:border-foreground/30 hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
						aria-label="View on-chain provenance"
					>
						<span className="size-3.5 shrink-0">{sourceIcon}</span>
						<span className="hidden sm:inline">On-chain</span>
					</button>
				</PopoverTrigger>
			</TooltipTrigger>
			<TooltipContent side="top">
				<p className="text-xs">View on-chain provenance</p>
			</TooltipContent>
		</Tooltip>
	</TooltipProvider>
)

const Pill: FC<{ children: React.ReactNode }> = ({ children }) => (
	<span className="inline-flex min-w-0 items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 font-mono text-xs text-muted-foreground">
		{children}
	</span>
)

const CollectionObjectProvenance: FC<{
	inscription: any
	objectType: Exclude<CollectionObjectType, 'collection'>
	metaData: any
}> = ({ inscription, objectType, metaData }) => {
	const parentIds = useMemo(() => normalizeParentIds(inscription), [inscription])

	const inscriptionId =
		(objectType === 'gallery' ? metaData?.gallery_inscription_id : metaData?.parent_inscription_id) ||
		inscription?.id
	const blockHeight = firstNumber(inscription?.genesis_height)
	const inscriptionNum = firstNumber(inscription?.num)
	const objectLabel = objectType === 'gallery' ? 'Gallery' : 'Parent collection'
	const sourceLabel = objectType === 'gallery' ? 'Gallery manifest' : 'Parent-child'
	const sourceIcon =
		objectType === 'gallery' ? (
			<Images className="size-full" strokeWidth={2.2} />
		) : (
			<GitBranch className="size-full" strokeWidth={2.2} />
		)
	const description =
		objectType === 'gallery'
			? "This market uses the inscription's on-chain gallery manifest as its membership source."
			: 'This market uses inscriptions that point to this inscription as their parent.'

	return (
		<Popover>
			<HeaderButton sourceIcon={sourceIcon} />
			<PopoverContent
				align="start"
				side="bottom"
				sideOffset={8}
				className="w-[calc(100vw_-_24px)] max-w-[380px] rounded-md border-border bg-card p-0 text-foreground shadow-lg"
			>
				<div className="flex items-start gap-3 border-b border-border px-4 py-3">
					<div className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-foreground">
						<ShieldCheck className="size-4" strokeWidth={2.1} />
					</div>
					<div className="min-w-0">
						<div className="flex flex-wrap items-center gap-2">
							<div className="text-sm font-semibold leading-none text-foreground">
								On-chain provenance
							</div>
							<span className="rounded-full border border-border bg-background px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
								{objectLabel}
							</span>
						</div>
						<p className="mt-1 text-xs leading-5 text-muted-foreground">{description}</p>
					</div>
				</div>

				<div className="px-4 py-1">
					<DetailRow label="Source">
						<div className="flex items-center gap-1.5">
							<span className="size-3.5 text-muted-foreground">{sourceIcon}</span>
							<span>{sourceLabel}</span>
						</div>
					</DetailRow>

					<DetailRow label="Inscription">
						<div className="flex min-w-0 flex-wrap items-center gap-2">
							<Pill>
								<Hash className="size-3.5 shrink-0" />
								<span className="truncate">{truncateId(inscriptionId, 10, 8)}</span>
							</Pill>
							{inscriptionId && (
								<a
									href={`${ORD_ROOT_URL}/inscription/${inscriptionId}`}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-foreground/30 hover:bg-accent hover:text-foreground"
								>
									ORD
									<ExternalLink className="size-3.5" />
								</a>
							)}
						</div>
					</DetailRow>

					<DetailRow label="Number">
						{typeof inscriptionNum === 'number' ? `#${integerNumber.format(inscriptionNum)}` : '-'}
					</DetailRow>

					<DetailRow label="Block">{formatInteger(blockHeight)}</DetailRow>

					{parentIds.length > 0 && (
						<DetailRow label="Lineage">
							<div className="flex min-w-0 flex-col gap-2">
								<div className="flex items-center gap-1.5 text-muted-foreground">
									<Layers3 className="size-3.5" />
									<span>{parentIds.length} parent{parentIds.length === 1 ? '' : 's'}</span>
								</div>
								<div className="flex min-w-0 flex-wrap gap-2">
									{parentIds.slice(0, 6).map((parentId, index) => (
										<Link
											key={parentId}
											href={`/inscription/${encodeURIComponent(parentId)}`}
											className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-xs font-medium text-foreground transition-colors hover:border-foreground/30 hover:bg-accent"
										>
											<ArrowUpRight className="size-3.5 shrink-0 text-muted-foreground" />
											<span>{index === 0 ? 'Parent' : `Parent ${index + 1}`}</span>
											<span className="min-w-0 truncate font-mono text-muted-foreground">
												{truncateId(parentId)}
											</span>
										</Link>
									))}
									{parentIds.length > 6 && (
										<span className="inline-flex items-center rounded-md border border-border px-2 py-1 text-xs text-muted-foreground">
											+{parentIds.length - 6} more
										</span>
									)}
								</div>
							</div>
						</DetailRow>
					)}
				</div>

				{inscriptionId && (
					<a
						href={`${ORD_ROOT_URL}/inscription/${inscriptionId}`}
						target="_blank"
						rel="noopener noreferrer"
						className="flex items-center justify-between border-t border-border px-4 py-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
					>
						<span>Open defining inscription on ORD</span>
						<SquareArrowOutUpRight className="size-3.5" />
					</a>
				)}
			</PopoverContent>
		</Popover>
	)
}

export default CollectionObjectProvenance
